import { Group, Line } from 'react-konva'
import { KonvaEventObject } from 'konva/lib/Node'
import { Door2D, Wall2D } from '../../../models/Construction'
import { useCallback, useEffect, useState } from 'react'
import { BaseComponentProps } from './EditorWrapper'

interface DoorComponentProps extends BaseComponentProps<Door2D> {
  wall: Wall2D
}

export const DoorComponent: React.FC<DoorComponentProps> = ({
  wall,
  element,
  scale,
  thicknessMultiplier = 1,
  isSelected = false,
  isHovered = false,
  isHidden = false,
  onSelect,
  onResizeStart,
}) => {
  const [isDoorHovered, setIsDoorHovered] = useState(isHovered)

  useEffect(() => {
    setIsDoorHovered(isHovered)
  }, [isHovered])

  const handleClick = useCallback((e: KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    onSelect?.(element);
  }, [element, onSelect]);

  // Calculate door position
  const centerX = wall.center_point[0] * scale
  const centerY = wall.center_point[1] * scale
  const wallAngle = wall.angle || 0
  const wallRadians = (wallAngle * Math.PI) / 180

  const offsetFromCenter = element.center_location_on_wall - wall.length / 2
  const doorCenterX = centerX + offsetFromCenter * Math.cos(wallRadians) * scale
  const doorCenterY = centerY + offsetFromCenter * Math.sin(wallRadians) * scale

  const doorWidth = element.width * scale
  const wallThickness = wall.thickness * scale * thicknessMultiplier

  // Calculate hinge position along the door opening
  const hingePosition = element.hinge_side || 'right'
  const hingeOffset = hingePosition === 'left' ? -doorWidth / 2 : doorWidth / 2
  const hingeX = doorCenterX + hingeOffset * Math.cos(wallRadians)
  const hingeY = doorCenterY + hingeOffset * Math.sin(wallRadians)

  // Position the hinge on the inside edge of the wall (room side)
  const wallEdgeOffset = wallThickness / 2
  const insideWallAngle = wallAngle - 90 // Direction toward inside of wall
  const insideWallRadians = (insideWallAngle * Math.PI) / 180

  // Move hinge to inside edge of wall
  const hingeInsideX = hingeX + wallEdgeOffset * Math.cos(insideWallRadians)
  const hingeInsideY = hingeY + wallEdgeOffset * Math.sin(insideWallRadians)

  // Calculate arc angles
  const swingAngle = 90
  const startAngle =
    wallAngle - 90 + (hingePosition === 'left' ? 0 : -swingAngle)
  const endAngle = startAngle + swingAngle

  // Calculate the open door end point (where door panel is when fully open)
  const openDoorAngle = endAngle // Door is fully open at the end of the arc
  const openDoorRadians = (openDoorAngle * Math.PI) / 180
  const openDoorEndX = hingeInsideX + doorWidth * Math.cos(openDoorRadians)
  const openDoorEndY = hingeInsideY + doorWidth * Math.sin(openDoorRadians)

  // Create smooth dashed arc using Line with points
  const createDashedArcPoints = () => {
    const points = []
    const numSegments = 30 // More segments for smoother arc
    const dashLength = 8 // Length of each dash
    const gapLength = 4 // Length of each gap
    let currentLength = 0
    let isDash = true
    let segmentLength = 0

    for (let i = 0; i <= numSegments; i++) {
      const progress = i / numSegments
      const angle = startAngle + (endAngle - startAngle) * progress
      const radians = (angle * Math.PI) / 180
      const x = hingeInsideX + doorWidth * Math.cos(radians)
      const y = hingeInsideY + doorWidth * Math.sin(radians)

      if (i > 0) {
        segmentLength += Math.sqrt(
          Math.pow(x - points[points.length - 2], 2) +
            Math.pow(y - points[points.length - 1], 2),
        )
      }

      // Add point if we're in a dash
      if (isDash) {
        points.push(x, y)
        if (segmentLength >= dashLength) {
          isDash = false
          segmentLength = 0
        }
      } else {
        // We're in a gap
        if (segmentLength >= gapLength) {
          isDash = true
          segmentLength = 0
          points.push(x, y) // Start new dash
        }
      }
    }

    return points
  }

  return (
    <Group>
      {/* Dashed arc using Line with calculated points */}
      <Line
        points={createDashedArcPoints()}
        stroke={isSelected ? '#FF0000' : isDoorHovered ? '#CD853F' : '#7C7C7C'}
        strokeWidth={isSelected ? 2 : 1}
        visible={!isHidden}
        lineCap='round'
        lineJoin='round'
        dash={[6, 4]} // Additional dashing for consistent appearance
        onClick={handleClick}
        onTap={handleClick}
        onMouseEnter={() => setIsDoorHovered(true)}
        onMouseLeave={() => setIsDoorHovered(false)}
      />

      {/* Single radial line from hinge to open door position (shows door when fully open) */}
      <Line
        points={[hingeInsideX, hingeInsideY, openDoorEndX, openDoorEndY]}
        visible={!isHidden}
        stroke={isSelected ? '#FF0000' : isDoorHovered ? '#CD853F' : '#000000'}
        strokeWidth={isSelected ? 2 : 1}
        onClick={handleClick}
        onTap={handleClick}
        onMouseEnter={() => setIsDoorHovered(true)}
        onMouseLeave={() => setIsDoorHovered(false)}
      />
    </Group>
  )
}
